'use client';

import { useEffect, PropsWithChildren, useRef } from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { useCurrentUser } from '@ianesthesie-kit/redux';
import { usePrevious } from '@ianesthesie-kit/core';
import { AppConfigService } from '@/config';
import { AvailableLocale } from '@ianesthesie-kit/config';

function IntercomBooter({ locale }: { locale: AvailableLocale }) {
  const { boot, update } = useIntercom();
  const currentUser = useCurrentUser();
  const previousLocale = usePrevious(locale);
  const isBooted = useRef(false);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    if (isBooted.current && previousLocale !== locale && previousLocale) {
      update({
        languageOverride: locale
      });

      return;
    }

    let t: ReturnType<typeof setTimeout>;
    if (!isBooted.current) {
      t = setTimeout(() => {
        boot({
          email: currentUser.email,
          userId: currentUser.id,
          name: `${currentUser.firstName} ${currentUser.lastName}`,
          phone: currentUser.phoneNumber,
          languageOverride: locale,
          customAttributes: {
            type: currentUser.type
          }
        });
        isBooted.current = true;
      }, 10);
    }

    return () => {
      if (t) {
        clearTimeout(t);
      }
    };
  }, [currentUser, boot, update, previousLocale, locale]);

  return null;
}

export function Intercom({
  children,
  locale
}: PropsWithChildren<{ locale: AvailableLocale }>) {
  if (!AppConfigService.getInstance().intercomEnabled) {
    return <>{children}</>;
  }

  return (
    <IntercomProvider appId={AppConfigService.getInstance().intercomAppId}>
      {children}
      <IntercomBooter locale={locale} />
    </IntercomProvider>
  );
}
